import * as React from 'react';

import { Button } from '../button/button.tsx';
import { LinearProgress } from '../linear-progress/linear-progress.tsx';

import { useDevice, useWindowSize } from '../hooks.tsx';

import './section-header.css';

interface SectionHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  sectionName: string;
  /** One line of secondary text above the title; while set, its row keeps its height even when empty. */
  eyebrow?: React.ReactNode;
  progress?: number;
  progressText?: string;
  buttonPosition: 'left' | 'right' | 'hidden';
  buttonText?: string;
  buttonLeadingIcon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Forwarded to LinearProgress so a form holding an unsaved edit is not shown as done. */
  needsAttention?: boolean;
}

const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  (
    {
      sectionName,
      eyebrow,
      progress,
      progressText,
      buttonText,
      buttonPosition,
      buttonLeadingIcon,
      onClick,
      needsAttention = false,
      ...props
    },
    ref,
  ) => {
    const dimensions = useWindowSize();
    const device = useDevice(dimensions);
    return (
      <div className="section-header" ref={ref} {...props}>
        <div className="section-header-content">
          <div className="section-header-titles">
            {eyebrow !== undefined && (
              <div className="section-header-eyebrow">{eyebrow}</div>
            )}
            <span className="section-header-name" title={sectionName}>
              {sectionName}
            </span>
          </div>
          {buttonPosition === 'left' && device === 'desktop' && (
            <div className="section-header-button-l">
              <Button
                kind="default"
                variant="secondary"
                size="regular"
                onClick={onClick}
              >
                {buttonText}
              </Button>
            </div>
          )}
        </div>
        {progress !== undefined &&
          progressText !== undefined &&
          buttonPosition !== 'right' &&
          device === 'desktop' && (
            <div className="section-header-progress">
              <LinearProgress
                progress={progress}
                progressText={progressText}
                needsAttention={needsAttention}
              />
            </div>
          )}
        {buttonPosition === 'right' && (
          <div className="section-header-button-r">
            <Button
              kind="default"
              variant="secondary"
              onClick={onClick}
              size={device === 'mobile' ? 'small' : 'regular'}
              leadingIcon={buttonLeadingIcon}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    );
  },
);

export { SectionHeader };
